import { useState, useRef, useCallback } from 'react';
import { trackPDFUploaded } from '../../utils/analytics';
import {
  MdUploadFile,
  MdClose,
  MdCheckCircle,
  MdError,
  MdDescription
} from 'react-icons/md';

const UploadFile = MdUploadFile as any;
const X = MdClose as any;
const CheckCircle = MdCheckCircle as any;
const Error = MdError as any;
const Description = MdDescription as any;

interface PDFUploaderProps {
  onUpload: (files: File[], mode: 'reset' | 'append') => Promise<void>;
  disabled?: boolean;
  hasExistingContext?: boolean;
  maxFiles?: number;
}

export default function PDFUploader({
  onUpload,
  disabled = false,
  hasExistingContext = false,
  maxFiles = 5
}: PDFUploaderProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showModeDialog, setShowModeDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = (files: File[]): { valid: File[], errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB

    files.forEach(file => {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        errors.push(`${file.name}: Only PDF files are allowed`);
      } else if (file.size > MAX_SIZE) {
        errors.push(`${file.name}: File exceeds 10MB limit`);
      } else {
        valid.push(file);
      }
    });

    return { valid, errors };
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const { valid, errors } = validateFiles(fileArray);

    if (errors.length > 0) {
      setUploadError(errors.join('; '));
      setTimeout(() => setUploadError(null), 5000);
    }

    if (valid.length > 0) {
      const totalFiles = selectedFiles.length + valid.length;
      if (totalFiles > maxFiles) {
        setUploadError(`Maximum ${maxFiles} files allowed`);
        setTimeout(() => setUploadError(null), 5000);
        return;
      }

      setSelectedFiles(prev => [...prev, ...valid]);
      setUploadError(null);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isUploading) return;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [disabled, isUploading]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value so the same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadClick = () => {
    if (selectedFiles.length === 0) return;

    if (hasExistingContext) {
      setShowModeDialog(true);
    } else {
      performUpload('reset');
    }
  };

  const performUpload = async (mode: 'reset' | 'append') => {
    setShowModeDialog(false);
    setIsUploading(true);
    setUploadError(null);

    try {
      await onUpload(selectedFiles, mode);
      trackPDFUploaded(selectedFiles.length);
      setSelectedFiles([]);
    } catch (error: any) {
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : disabled || isUploading
            ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-blue-400 cursor-pointer'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || isUploading}
        />

        <UploadFile className="mx-auto h-12 w-12 text-gray-400 mb-3" />

        <p className="text-sm text-gray-600 mb-1">
          Drag and drop PDF files here, or click to browse
        </p>
        <p className="text-xs text-gray-500">
          Maximum {maxFiles} files, 10MB each
        </p>
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <Error className="h-5 w-5 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Selected Files ({selectedFiles.length})
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Description className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                {!isUploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUploadClick}
            disabled={disabled || isUploading || selectedFiles.length === 0}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
              disabled || isUploading || selectedFiles.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Mode Selection Dialog */}
      {showModeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Lesson Context Already Exists
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              You already have lesson context materials. Would you like to replace them or add to them?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => performUpload('reset')}
                className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Replace (Clear existing context)
              </button>
              <button
                onClick={() => performUpload('append')}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Add to Existing Context
              </button>
              <button
                onClick={() => setShowModeDialog(false)}
                className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
